package one.devsbox.shared.ads

import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable
import one.devsbox.commons.EMPTY
import one.devsbox.commons.KdbLocalDateTimeSerializer
import one.devsbox.shared.devices.IO
import one.devsbox.shared.files.ResourceType
import one.devsbox.shared.msg.MessageBody

object SharedIO {

    @Serializable
    data class UserSimple(
        var id: Long,
        var name: String,
        @Serializable(with = KdbLocalDateTimeSerializer::class)
        var activity: LocalDateTime,
        var nameCompany: String = ""
    ) {
        companion object {}
    }

    @Serializable
    data class Users(
        /**
         * actual page
         * */
        override var page: Int = 0,
        /**
         * total media
         * */
        override var count: Int = 0,
        /**
         * media , one page max 100 items
         * */
        override var items: List<UserSimple> = emptyList()
    ) : DataHolder<UserSimple> {}

    @Serializable
    data class UserActivity(
        var idUser: Long,
        var idTarget: Long,
        @Serializable(with = KdbLocalDateTimeSerializer::class)
        var dt: LocalDateTime,
        var action: Int,
        var target: Int,
        var name: String
    ) {
        companion object {}
    }

    @Serializable
    data class UserActivities(
        override var page: Int = 0,
        override var count: Int = 0,
        override var items: List<UserActivity> = emptyList()
    ) : DataHolder<UserActivity> {}

    @Serializable
    data class Hosts(
        var main: String = "",
        var storage: String = "",
        var msg: String = "",
        var device: String = "",
    ) {}

    @Serializable
    data class Campaigns(
        /**
         * actual page
         * */
        override var page: Int = 0,
        /**
         * total media
         * */
        override var count: Int = 0,
        /**
         * media , one page max 100 items
         * */
        override var items: List<CampaignSimple> = emptyList()
    ) : DataHolder<CampaignSimple> {}

    @Serializable
    data class Permission(
        var read: Int = 0,
        var write: Int = 0,
        var delete: Int = 0,
    ) {
        companion object {}

        fun wrapper() = PermissionWrapper(this)

        @Suppress("unused")
        class PermissionWrapper(permission: Permission) {

            val user = Item(TargetType.USER, permission)
            val media = Item(TargetType.RESOURCE, permission)
            val device = Item(TargetType.DEVICE, permission)
            val location = Item(TargetType.LOCATION, permission)
            val campaign = Item(TargetType.CAMPAIGN, permission)
            val company = Item(TargetType.COMPANY, permission)

            class Item(target: TargetType, private val permission: Permission) {
                val read = permission.read and target.mask > 0
                var write = permission.write and target.mask > 0
                var delete = permission.delete and target.mask > 0

                val all get() = listOf(read, write, delete)
            }

            val all get() = listOf(user, media, device, location, campaign)

            fun canGrant(wrapper: PermissionWrapper): Boolean {
                return all.mapIndexed { indexItemMy, itemMy ->
                    wrapper.all[indexItemMy].all.mapIndexed { indexType, granted ->
                        !granted || (granted && itemMy.all[indexType])
                    }.all { true }
                }.all { true }
            }
        }
    }

    @Serializable
    data class UserNew(
        var name: String,
        var login: String,
        var permission: Permission = Permission(),
    ) {
        companion object {}
    }

    @Serializable
    data class User(
        var id: Long = -1,
        var name: String = "",
        var login: String = "",
        @Serializable(with = KdbLocalDateTimeSerializer::class)
        var activity: LocalDateTime = LocalDateTime.EMPTY,
        var permission: Permission = Permission(),
        var company: CompanyDef = CompanyDef(),
    ) {
        companion object {}

        val admin get() = permission.wrapper().company.read
        val adminMode get() = company.working == company.primary && admin
        val workMode get() = company.working != company.primary && admin


        @Serializable
        data class CompanyDef(
            var primary: Long = -1,
            var working: Long = -1,
            var workingName: String = "",
            var primaryName: String = "",

            ) {}
    }

    @Serializable
    data class Companies(
        override var page: Int,
        override var count: Int,
        override var items: List<CompanySimple> = listOf()
    ) : DataHolder<CompanySimple> {}

    @Serializable
    data class CompanySimple(
        var id: Long,
        var name: String,
        var counter: Counter = Counter(),
    ) {
        companion object {}

        @Serializable
        data class Counter(
            var device: Int = 0,
            var location: Int = 0,
            var user: Int = 0,
        ) {}
    }

    @Serializable
    data class Locations(
        override var page: Int,
        override var count: Int,
        override var items: List<LocationSimple> = listOf()
    ) : DataHolder<LocationSimple> {}

    /**/
    @Serializable
    data class LocationSimple(
        var id: Long,
        var companyId: Long,
        var name: String,
        var address: String,
        var devices: Int,
        var nameCompany: String = ""
    ) {
        companion object {}
    }

    @Serializable
    data class LocationsWithDevices(
        override var page: Int,
        override var count: Int,
        override var items: List<LocationWithDevices>
    ) : DataHolder<LocationWithDevices> {}

    @Serializable
    data class LocationWithDevices(
        var id: Long,
        var companyId: Long,
        var name: String,
        var devices: List<Devices> = emptyList()
    ) {
        companion object {}

        @Serializable
        data class Devices(
            var id: Long,
            var name: String,
        ) {
            companion object {}
        }
    }

    @Serializable
    data class LocationNew(
        var name: String,
        var address: String,
        val oh: List<MessageBody.Config.OpeningHour> = MessageBody.Config.OpeningHour.all()
    ) {
        companion object {}
    }

    @Serializable
    data class Location(
        var id: Long = -1,
        var companyId: Long,
        var name: String = "",
        var address: String = "",
        val oh: List<MessageBody.Config.OpeningHour> = MessageBody.Config.OpeningHour.all(),
        val devices: List<DeviceSimple> = listOf()
    ) {
        companion object {}
    }

    /**/

    interface MediaCore {
        var id: Long
        var type: Int
        var name: String
        var aspectRatio: String
        var duration: Long
        var width: Int
        var height: Int
    }

    interface DataHolder<T> {
        /**
         * actual page
         * */
        var page: Int

        /**
         * total
         * */
        var count: Int

        /**
         * media , one page max 100 items
         * */
        var items: List<T>
    }

    @Serializable
    data class Resources(
        /**
         * actual page
         * */
        override var page: Int = 0,
        /**
         * total media
         * */
        override var count: Int = 0,
        /**
         * media , one page max 100 items
         * */
        override var items: List<MediaSimple> = emptyList()
    ) : DataHolder<MediaSimple> {}

    @Serializable
    data class MediaSimple(
        override var id: Long,
        var companyId: Long,
        override var type: Int,
        var error: Boolean,
        override var name: String,
        override var aspectRatio: String,
        override var duration: Long,
        override var width: Int,
        override var height: Int,
        @Serializable(with = KdbLocalDateTimeSerializer::class)
        var create: LocalDateTime = LocalDateTime.EMPTY,
        @Serializable(with = KdbLocalDateTimeSerializer::class)
        var ready: LocalDateTime = LocalDateTime.EMPTY,
        var nameCompany: String = ""
    ) : MediaCore {
        companion object {}
    }


    @Serializable
    data class Media(
        override var id: Long,
        var companyId: Long,
        override var type: Int,
        var error: Boolean,
        override var name: String,
        override var aspectRatio: String,
        override var duration: Long,
        override var width: Int,
        override var height: Int,

        var colors: String = "",
        var tags: String = "",
        var description: String = "",

        var pages: Int,
        var size: Long,
        @Serializable(with = KdbLocalDateTimeSerializer::class)
        val createDT: LocalDateTime,
        @Serializable(with = KdbLocalDateTimeSerializer::class)
        var readyDT: LocalDateTime,
        var err: String
    ) : MediaCore {
        companion object {}
    }


    @Serializable
    data class Devices(
        override var page: Int = 0,
        var online: Int = 0,
        var ids: List<Long> = emptyList(),
        override var count: Int = ids.size,
        override var items: List<DeviceSimple> = emptyList(),
    ) : DataHolder<DeviceSimple> {}

    /**/
    @Serializable
    data class DeviceSimple(
        var id: Long,
        val photoId: Long,
        var locationId: Long,
        var name: String,
        var nameLocation: String,
        var aspectRatio: String,
        var campaigns: Int = 0,
        var address: String = "",
        /**
         * @see DeviceType
         * */
        var type: Int = DeviceType.INDOOR.mask,
        var online: Boolean = false,
        var nameCompany: String = ""
    ) {
        companion object {}
    }

    @Serializable
    data class Device(
        var content: IO.Device,
        var info: DeviceSimple
    ) {
        companion object {}
    }

    @Serializable
    data class DeviceChange(
        var locationId: Long,
        var photoId: Long,
        var name: String,
        var address: String,
        var screenW: Int,
        var screenH: Int,
        var screenR: Int,
        var aspectRatio: String,
        var deviceType: Int = DeviceType.INDOOR.mask
    ) {
        companion object {}

        fun remoteChanges(origin: IO.Device) =
            screenW != origin.config.screenW ||
                    screenH != origin.config.screenH ||
                    screenR != origin.config.screenR
    }

    @Serializable
    data class CampaignSimple(
        var id: Long,
        var companyId: Long,
        var name: String,
        var dStart: LocalDate,
        var dEnd: LocalDate,
        /**
         * @see CampaignState
         */
        var state: Int,
        var media: List<Long> = listOf(),
        var countDevice: Int = 0,
        var countLocation: Int = 0,
        var impressions: Impressions = Impressions(),
        var nameCompany: String = ""
    ) {
        companion object {}
    }

    interface ImplCampaign<M : ImplCampaign.ImplVisual> {
        var name: String
        var dStart: LocalDate
        var dEnd: LocalDate
        var state: Int
        var visual: List<M>

        interface ImplVisual {
            var id: Long
            var duration: Long
            var devices: Set<Long>
        }
    }

    @Serializable
    data class CampaignCore(
        override var name: String,
        override var dStart: LocalDate,
        override var dEnd: LocalDate,
        /**
         * @see CampaignState
         */
        override var state: Int,
        override var visual: List<Entry> = emptyList()
    ) : ImplCampaign<CampaignCore.Entry> {

        companion object {}

        @Serializable
        data class Entry(
            override var id: Long,
            override var duration: Long,
            override var devices: Set<Long> = emptySet()

        ) : ImplCampaign.ImplVisual {
            companion object {}
        }
    }

    @Serializable
    data class Impressions(
        var all: Long = 0,
        var today: Long = 0
    ) {}

    @Serializable
    data class Campaign(
        var id: Long = -1,
        var companyId: Long = -1L,
        override var name: String,
        override var dStart: LocalDate,
        override var dEnd: LocalDate,
        /**
         * @see CampaignState
         */
        override var state: Int,
        override var visual: List<Media> = listOf(),
        var impressions: Impressions = Impressions()
    ) : ImplCampaign<Campaign.Media> {

        companion object {}

        @Serializable
        data class Media(
            override var id: Long,
            override var name: String,
            override var aspectRatio: String,
            /**
             * @see one.devsbox.shared.files.ResourceType
             */
            override var type: Int,
            override var duration: Long,
            override var devices: Set<Long> = emptySet(),
            override var width: Int,
            override var height: Int
        ) : ImplCampaign.ImplVisual, MediaCore {
            companion object {}
        }
    }

    @Serializable
    data class CampaignInfo(
        var id: Long = -1,
        var companyId: Long = -1,
        override var name: String,
        override var dStart: LocalDate,
        override var dEnd: LocalDate,
        /**
         * @see CampaignState
         */
        override var state: Int,
        override var visual: List<Campaign.Media> = listOf(),
        var location: List<Location> = listOf(),
        var device: List<Device> = listOf(),
        var impressions: Impressions = Impressions(),
    ) : ImplCampaign<Campaign.Media> {

        companion object {}

        @Serializable
        data class Location(
            var id: Long,
            var name: String,
        ) {}

        @Serializable
        data class Device(
            var id: Long,
            var id_location: Long,
            var name: String,
            var online: Boolean = false
        ) {}
    }

    @Serializable
    data class Playlist(
        var deviceId: Long = 0L,
        var campaign: List<Campaign> = emptyList()
    ) {
        companion object {}

        @Serializable
        data class Campaign(
            var id: Long,
            var visual: Visual,
            var start: LocalDate = LocalDate.EMPTY,
            var end: LocalDate = LocalDate.EMPTY,
        ) {
            companion object {}
        }

        @Serializable
        data class Visual(
            var id: Long,
            /**
             * @see ResourceType
             */
            var type: Int,
            var duration: Long = 1000
        ) {
            companion object {}
        }
    }
}

